import { CopyOutlined, ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, message, Modal, Space, Spin, Typography } from 'antd';
import { buildShortsWorkOrderFeedUrl } from 'constants/api';
import React, { useCallback, useEffect, useState } from 'react';
import { WORK_ORDER_MAX_ITEMS, WORK_ORDER_WINDOW_DAYS } from '../constants';
import { formatPresetErrorMessage, shortsPresetService } from '../services/shortsPresetService';

const { Text, Link } = Typography;

/** `<item>` occurrences in the feed. Cheap on purpose — the vendor does the real parsing. */
const ITEM_TAG_PATTERN = /<item[\s>]/g;

const countItems = (xml: string): number => (xml.match(ITEM_TAG_PATTERN) ?? []).length;

interface WorkOrderFeedModalProps {
  open: boolean;
  onClose: () => void;
  /** Feed owner. One playlist is one channel is one feed (spec §5.3). */
  playlistId: number;
  /** Whether the playlist has a preset linked — without one the feed carries no order. */
  hasPreset: boolean;
}

/**
 * Read-only view of one playlist's work-order MRSS feed.
 *
 * Fetched through axios rather than iframed so the XML can be shown with an in-app
 * explanation of what an empty feed means; the endpoint itself is public, so the
 * "open in a new tab" link works too.
 */
const WorkOrderFeedModal: React.FC<WorkOrderFeedModalProps> = ({
  open,
  onClose,
  playlistId,
  hasPreset,
}) => {
  const [xml, setXml] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const feedUrl = buildShortsWorkOrderFeedUrl(playlistId);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const feed = await shortsPresetService.fetchWorkOrderFeed(playlistId);
      setXml(feed);
    } catch (err) {
      // The global axios interceptor already surfaces standard error payloads;
      // keep this inline so the user isn't hit with two dialogs.
      setError(formatPresetErrorMessage(err, 'Failed to load the work order feed'));
      setXml('');
    } finally {
      setLoading(false);
    }
  }, [playlistId]);

  useEffect(() => {
    if (open) {
      loadFeed();
    } else {
      setXml('');
      setError('');
    }
  }, [open, loadFeed]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(xml);
      message.success('Copied to clipboard');
    } catch {
      message.error('Could not copy — select the text and copy manually');
    }
  };

  const itemCount = countItems(xml);

  return (
    <Modal
      title="Shorts Work Order Feed"
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Space style={{ marginBottom: '12px' }} wrap>
        <Button icon={<ReloadOutlined />} onClick={loadFeed} loading={loading}>
          Reload
        </Button>
        <Button icon={<CopyOutlined />} onClick={handleCopy} disabled={!xml}>
          Copy XML
        </Button>
        <Link href={feedUrl} target="_blank">
          <ExportOutlined /> Open in a new tab
        </Link>
      </Space>

      <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
        Episodes selected by this playlist&apos;s rules that aired within the last{' '}
        {WORK_ORDER_WINDOW_DAYS} days, oldest first, {WORK_ORDER_MAX_ITEMS} at most. The window is
        fixed — there is no parameter to widen it.
      </Text>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: '12px' }} />}

      {!error && !loading && !hasPreset && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: '12px' }}
          message="This playlist has no preset, so the feed carries no <odk:order>."
          description={
            'The vendor can read the episodes but has no spec to generate against. ' +
            'Link a preset on this page.'
          }
        />
      )}

      {!error && !loading && xml && itemCount === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: '12px' }}
          message="No episodes in the current window — this is a normal state, not an error."
          description={
            'The feed is a rolling ' +
            `${WORK_ORDER_WINDOW_DAYS}-day window, not an archive (spec §7.2). Once episodes of ` +
            'this playlist air, they appear here and drop out again as they age past the window.'
          }
        />
      )}

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        !error && (
          <pre
            style={{
              maxHeight: '55vh',
              overflow: 'auto',
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: '4px',
              padding: '12px',
              margin: 0,
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {xml || 'The feed returned an empty body.'}
          </pre>
        )
      )}
    </Modal>
  );
};

export default WorkOrderFeedModal;
